import React from 'react';
import { Card } from '@/components/ui/cards/Card';
import { Text } from '@/components/ui/text/Text';
import { colors } from '@/assets/styles/colors';
import type { AttendanceStatus } from '@/api/domains/attendance';


interface DisplayMetric {
  label: string;
  value: string;
}

interface AttendanceSituationCardProps {
  status: AttendanceStatus;
  statusLabel: string;
  scheduleTheoretical: string;
  scheduleReal: string;
  metrics: DisplayMetric[];
  dateLabel?: string;
  // Slot opcional renderizado en la esquina superior derecha de la tarjeta (p. ej. el
  // trigger de un `ActionMenu`). Genérico por diseño, mismo patrón que `DonutSummaryCard`.
  headerAction?: React.ReactNode;
  'data-testid'?: string;
}

interface StatusColors {
  fg: string;
  bg: string;
}

const STATUS_COLORS: Record<AttendanceStatus, StatusColors> = {
  presente:           { fg: colors.feedback.success300, bg: colors.dashboardStatus.presenteBg },
  ausente:            { fg: colors.feedback.error300,   bg: colors.dashboardStatus.ausenteBg },
  advertencia:        { fg: colors.feedback.alert300,   bg: colors.dashboardStatus.advertenciaBg },
  ausentismo:         { fg: colors.important.main,      bg: colors.dashboardStatus.ausentismoBg },
  ausente_justificado:{ fg: colors.important.main,      bg: colors.dashboardStatus.ausentismoBg },
  sin_marcaje:        { fg: colors.grays.neutral99,     bg: colors.dashboardStatus.sinMarcajeBg },
};

const StatusIndicator: React.FC<{ status: AttendanceStatus; 'data-testid'?: string }> = ({
  status,
  'data-testid': dataTestId,
}) => {
  const { fg, bg } = STATUS_COLORS[status] ?? STATUS_COLORS.sin_marcaje;
  return (
    <div
      style={{
        position: 'relative',
        width: 34,
        height: 31,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: bg,
        borderRadius: '50%',
      }}
      data-testid={dataTestId}
    >
      <div
        style={{
          width: 28,
          height: 26,
          borderRadius: '50%',
          backgroundColor: fg,
        }}
      />
    </div>
  );
};

export const AttendanceSituationCard: React.FC<AttendanceSituationCardProps> = ({
  status,
  statusLabel,
  scheduleTheoretical,
  scheduleReal,
  metrics,
  dateLabel,
  headerAction,
  'data-testid': dataTestId,
}) => (
  <Card elevation={2} borderRadius="16px" padding="0" data-testid={dataTestId}>
    {headerAction && (
      <div
        style={{ display: 'flex', justifyContent: 'flex-end', padding: '16px 16px 0' }}
        data-testid={`${dataTestId}-header-action`}
      >
        {headerAction}
      </div>
    )}
    <div style={{ display: 'flex', alignItems: 'center', padding: '24px 32px', gap: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1 }}>
        <StatusIndicator status={status} data-testid={`${dataTestId}-status-dot`} />

        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <Text
            variant="header-4"
            fontFamily="montserrat"
            color={colors.grays.neutral66}
            data-testid={`${dataTestId}-status-label`}
          >
            {statusLabel}
          </Text>
          {dateLabel && (
            <Text
              variant="body-medium"
              fontFamily="montserrat"
              color={colors.grays.neutral66}
              data-testid={`${dataTestId}-date-label`}
            >
              {dateLabel}
            </Text>
          )}
          <Text
            variant="body-medium"
            fontFamily="montserrat"
            color={colors.grays.neutral66}
            data-testid={`${dataTestId}-schedule-theoretical`}
          >
            {scheduleTheoretical}
          </Text>
          <Text
            variant="body-medium"
            fontFamily="montserrat"
            color={colors.grays.neutral66}
            data-testid={`${dataTestId}-schedule-real`}
          >
            {scheduleReal}
          </Text>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center' }}>
        {metrics.map((metric, index) => (
          <React.Fragment key={metric.label}>
            {index > 0 && (
              <div
                style={{
                  width: 4,
                  height: 93,
                  backgroundColor: colors.grays.neutralE5,
                  borderRadius: 2,
                  flexShrink: 0,
                }}
              />
            )}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
                gap: 4,
                padding: '0 32px',
                minWidth: 160,
              }}
              data-testid={`${dataTestId}-metric-${index}`}
            >
              <Text
                variant="header-4"
                fontFamily="montserrat"
                color={colors.grays.neutral66}
                style={{ fontSize: 40 }}
                data-testid={`${dataTestId}-metric-${index}-value`}
              >
                {metric.value}
              </Text>
              <Text
                variant="body-medium"
                fontFamily="montserrat"
                color={colors.grays.neutral66}
                data-testid={`${dataTestId}-metric-${index}-label`}
              >
                {metric.label}
              </Text>
            </div>
          </React.Fragment>
        ))}
      </div>
    </div>
  </Card>
);
